import { useEffect, useRef, useState } from 'react';
import { Play, X } from 'lucide-react';

/**
 * VideoCarousel — Iter-51.55.
 *
 * One YouTube video at a time. Auto-advances every 6s (paused while
 * the lightbox is open). Dot indicators below. Tapping the frame opens
 * a full-screen lightbox modal with the YouTube IFrame embed using
 * rel=0 + modestbranding=1 so end-of-video suggestions stay in-channel.
 *
 * `items` shape: [{ testimonial_id, video_id, video_title, featured }]
 */
export default function VideoCarousel({ items }) {
  const [idx, setIdx] = useState(0);
  const [openId, setOpenId] = useState(null);
  const timerRef = useRef(null);
  const active = items?.[idx];

  useEffect(() => {
    if (!items || items.length <= 1 || openId) return;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearTimeout(timerRef.current);
  }, [idx, items, openId]);

  if (!items || items.length === 0) return null;
  return (
    <div data-testid="video-carousel" className="w-full">
      <button
        data-testid={`video-carousel-open-${active.testimonial_id}`}
        onClick={() => setOpenId(active.video_id)}
        className="relative w-full rounded-2xl overflow-hidden shadow-lg bg-black aspect-video"
        aria-label={`Play video: ${active.video_title || 'testimonial'}`}
      >
        {/* Poster: YouTube's mqdefault is the smallest 16:9 asset. */}
        <img
          src={`https://i.ytimg.com/vi/${active.video_id}/mqdefault.jpg`}
          alt={active.video_title || 'Family review video'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
            <Play size={22} strokeWidth={3} className="text-[#FF0033] ml-1" fill="currentColor"/>
          </div>
        </div>
        {active.video_title && (
          <div className="absolute left-0 right-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/70 to-transparent">
            <div className="text-white text-xs font-bold text-left line-clamp-1">{active.video_title}</div>
          </div>
        )}
        {/* Iter-51.94 — visible Featured ribbon so the person who
            gave the testimonial (and every landing-page visitor) can
            see it was selected as Featured. Amber pill sits top-left
            with a shadow so it reads even against bright thumbnails. */}
        {active.featured && (
          <div
            data-testid={`video-carousel-featured-${active.testimonial_id}`}
            className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-amber-400 text-amber-950 text-[11px] font-extrabold uppercase tracking-wider px-2 py-0.5 shadow-lg ring-1 ring-amber-600/40"
          >
            <span aria-hidden="true">★</span> Featured
          </div>
        )}
      </button>

      {/* Dots — one per item; active dot is wider/orange. */}
      {items.length > 1 && (
        <div data-testid="video-carousel-dots" className="flex justify-center items-center gap-1.5 mt-2">
          {items.map((it, i) => (
            <button
              key={it.testimonial_id}
              data-testid={`video-carousel-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Show video ${i + 1} of ${items.length}`}
              className={
                'h-1.5 rounded-full transition-all ' +
                (i === idx ? 'w-6 bg-[#FF6B1A]' : 'w-1.5 bg-slate-300')
              }
            />
          ))}
        </div>
      )}

      {/* Lightbox modal — full-screen backdrop + embedded IFrame. */}
      {openId && (
        <div
          data-testid="video-carousel-modal"
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setOpenId(null)}
        >
          <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="video-carousel-close"
              onClick={() => setOpenId(null)}
              aria-label="Close video"
              className="absolute -top-10 right-0 text-white/80 hover:text-white p-2"
            >
              <X size={22} strokeWidth={2.5}/>
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl">
              <iframe
                title="Family review video"
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${openId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
